// src/schemas/auth.ts
import { z } from "zod";
var RegisterSchema = z.object({
  email: z.string().email().max(255),
  password: z.string().min(8).max(128),
  name: z.string().min(1).max(255).optional()
});
var LoginSchema = z.object({
  email: z.string().email(),
  password: z.string(),
  rememberMe: z.boolean().optional()
});
var ResetPasswordRequestSchema = z.object({
  email: z.string().email()
});
var ResetPasswordSchema = z.object({
  token: z.string(),
  password: z.string().min(8).max(128)
});
var QueueEmailSchema = z.object({
  type: z.enum([
    "verification",
    "password_reset",
    "welcome",
    "invitation",
    "contact_verification",
    "notification",
    "alert",
    "notification_preference_confirmation"
  ]),
  toEmail: z.string().email(),
  subject: z.string().max(255).optional(),
  body: z.string().optional(),
  metadata: z.record(z.string(), z.any()).optional()
});
var InviteToOrganizationSchema = z.object({
  email: z.string().email(),
  role: z.enum(["admin", "member"]),
  organizationId: z.string().uuid()
});

// src/schemas/billing.ts
import { z as z2 } from "zod";
var BillingSubscriptionStatusSchema = z2.enum([
  "incomplete",
  "incomplete_expired",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "paused"
]);
var BillingUsageKindSchema = z2.enum(["app_yearly", "sms", "call_minute", "seat"]);
var UsageEventStatusSchema = z2.enum(["pending", "posted", "failed"]);
var CreateCheckoutSessionBodySchema = z2.object({
  successUrl: z2.string().url().optional(),
  cancelUrl: z2.string().url().optional()
}).partial().optional();
var CreateCheckoutSessionResponseSchema = z2.object({
  url: z2.string().url()
});
var CreateBillingPortalSessionBodySchema = z2.object({
  returnUrl: z2.string().url().optional()
}).partial().optional();
var CreateBillingPortalSessionResponseSchema = z2.object({
  url: z2.string().url()
});
var RecordUsageBodySchema = z2.object({
  kind: BillingUsageKindSchema,
  quantity: z2.number().int().positive(),
  eventTime: z2.union([z2.string().datetime(), z2.date()]).optional(),
  idempotencyKey: z2.string().min(1),
  stripeRequestId: z2.string().min(1).optional()
});
var RecordUsageResponseSchema = z2.object({
  id: z2.string().uuid(),
  status: UsageEventStatusSchema
});
var BillingStatusResponseSchema = z2.object({
  stripeCustomerId: z2.string().nullable(),
  stripeSubscriptionId: z2.string().nullable(),
  subscriptionStatus: BillingSubscriptionStatusSchema.nullable(),
  currentPeriodEnd: z2.string().datetime({ offset: true }).nullable(),
  hasActiveSubscription: z2.boolean()
});
var PostUsageBatchResponseSchema = z2.object({
  processed: z2.number().int().nonnegative(),
  posted: z2.number().int().nonnegative(),
  failed: z2.number().int().nonnegative(),
  blocked: z2.number().int().nonnegative(),
  nextCursor: z2.string().nullable()
});
var StripeWebhookEventSchema = z2.object({
  id: z2.string(),
  type: z2.string(),
  data: z2.unknown(),
  created: z2.number().int(),
  livemode: z2.boolean().optional()
});

// src/schemas/commands/contact.ts
import { z as z3 } from "zod";
var CreateContactSchema = z3.object({
  organizationId: z3.string().min(1),
  name: z3.string().min(1).max(255),
  type: z3.enum(["email", "phone", "telegram"]),
  email: z3.string().email().optional().nullable(),
  phone_number: z3.string().min(1).max(50).optional().nullable(),
  telegram_chat_id: z3.string().min(1).max(64).optional().nullable()
}).refine(
  (data) => {
    if (data.type === "email") {
      return data.email !== null && data.email !== void 0;
    }
    if (data.type === "phone") {
      return data.phone_number !== null && data.phone_number !== void 0;
    }
    if (data.type === "telegram") {
      return true;
    }
    return true;
  },
  {
    message: "Email is required for email type, phone number is required for phone type"
  }
);
var UpdateContactSchema = z3.object({
  id: z3.number().int().positive(),
  organizationId: z3.string().min(1),
  name: z3.string().min(1).max(255).optional(),
  email: z3.string().email().optional().nullable(),
  phone_number: z3.string().min(1).max(50).optional().nullable(),
  telegram_chat_id: z3.string().min(1).max(64).optional().nullable()
});
var DeleteContactSchema = z3.object({
  id: z3.number().int().positive(),
  organizationId: z3.string().min(1)
});
var RequestVerificationSchema = z3.object({
  contactId: z3.number().int().positive(),
  organizationId: z3.string().min(1)
});
var RequestContactTestMessageSchema = z3.object({
  contactId: z3.number().int().positive(),
  organizationId: z3.string().min(1)
});
var SubmitVerificationCodeSchema = z3.object({
  contactId: z3.number().int().positive(),
  organizationId: z3.string().min(1),
  code: z3.string().min(6).max(6).regex(/^\d{6}$/, "Code must be 6 digits")
});

// src/schemas/commands/monitor.ts
import * as rrule from "rrule";
import { z as z4 } from "zod";
function hasRRuleExport(value) {
  return typeof value === "object" && value !== null && "RRule" in value;
}
var defaultExport = Reflect.get(rrule, "default");
var { RRule } = hasRRuleExport(defaultExport) ? defaultExport : rrule;
var RRuleStringSchema = z4.string().refine(
  (val) => {
    try {
      RRule.fromString(val);
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid RRULE string format" }
);
var TimezoneSchema = z4.string().refine(
  (val) => {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: val });
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid timezone identifier" }
);
var CreateMonitorSchema = z4.object({
  url: z4.string().url().max(2048),
  name: z4.string().min(1).max(255),
  rrule: RRuleStringSchema.optional().default("FREQ=MINUTELY;INTERVAL=5"),
  timezone: TimezoneSchema.optional().default("UTC"),
  dtstart: z4.date().optional(),
  exdate: z4.array(z4.string()).optional(),
  rdate: z4.array(z4.string()).optional(),
  enabled: z4.boolean().optional().default(true),
  organizationId: z4.string().min(1)
  // Better Auth uses string IDs, not UUIDs
});
var UpdateMonitorScheduleSchema = z4.object({
  rrule: RRuleStringSchema.optional(),
  timezone: TimezoneSchema.optional(),
  dtstart: z4.date().optional(),
  exdate: z4.array(z4.string()).optional(),
  rdate: z4.array(z4.string()).optional(),
  enabled: z4.boolean().optional()
});
var UpdateMonitorDetailsSchema = z4.object({
  name: z4.string().min(1).max(255).optional(),
  url: z4.string().url().max(2048).optional()
});
var CreateMeasurementSchema = z4.object({
  monitorId: z4.number().int().positive(),
  responseTimeMs: z4.number().int().positive().nullable(),
  httpStatusCode: z4.number().int().min(100).max(599).nullable(),
  responseBody: z4.string().max(2048).nullable(),
  // First 2KB
  errorMessage: z4.string().nullable(),
  success: z4.boolean()
});

// src/schemas/mcp.ts
import { z as z5 } from "zod";

// src/types/mcp.ts
var MCP_SCOPES = ["monitors:read", "monitors:write"];

// src/schemas/mcp.ts
var McpScopeSchema = z5.enum(MCP_SCOPES);
var McpConsentDecisionSchema = z5.object({
  csrfToken: z5.string().min(1).max(256),
  decision: z5.enum(["approve", "deny"]),
  organizationId: z5.string().min(1).max(128).optional(),
  scopes: z5.array(McpScopeSchema).min(1).max(2).optional()
}).strict();
var McpMonitorIdSchema = z5.number().int().positive().max(Number.MAX_SAFE_INTEGER);
var McpRequestIdSchema = z5.uuid();
var IsoDateSchema = z5.iso.datetime({ offset: true });
var McpScheduleShape = {
  rrule: CreateMonitorSchema.shape.rrule.pipe(z5.string().max(2048)),
  timezone: CreateMonitorSchema.shape.timezone.pipe(z5.string().max(100)),
  exdate: z5.array(z5.string().min(1).max(100)).max(100).optional(),
  rdate: z5.array(z5.string().min(1).max(100)).max(100).optional()
};
var PaginationShape = {
  limit: z5.number().int().min(1).max(100).optional().default(20),
  offset: z5.number().int().min(0).max(1e5).optional().default(0)
};
var McpListMonitorsSchema = z5.object(PaginationShape).strict();
var McpGetMonitorSchema = z5.object({ monitorId: McpMonitorIdSchema }).strict();
var McpGetMonitorHistorySchema = z5.object({
  monitorId: McpMonitorIdSchema,
  ...PaginationShape,
  startDate: IsoDateSchema.optional(),
  endDate: IsoDateSchema.optional()
}).strict().refine(
  ({ startDate, endDate }) => !startDate || !endDate || Date.parse(startDate) <= Date.parse(endDate),
  { message: "Start date must precede end date." }
);
var McpGetExecutionSchema = z5.object({ executionId: z5.uuid() }).strict();
var McpCreateMonitorSchema = CreateMonitorSchema.omit({ organizationId: true }).extend({ ...McpScheduleShape, requestId: McpRequestIdSchema, dtstart: IsoDateSchema.optional() }).strict();
var McpUpdateMonitorDetailsSchema = UpdateMonitorDetailsSchema.extend({
  monitorId: McpMonitorIdSchema,
  requestId: McpRequestIdSchema
}).strict();
var McpUpdateMonitorScheduleSchema = UpdateMonitorScheduleSchema.extend({
  ...McpScheduleShape,
  rrule: McpScheduleShape.rrule.optional(),
  timezone: McpScheduleShape.timezone.optional(),
  monitorId: McpMonitorIdSchema,
  requestId: McpRequestIdSchema,
  dtstart: IsoDateSchema.optional()
}).strict();
var McpMonitorMutationSchema = z5.object({ monitorId: McpMonitorIdSchema, requestId: McpRequestIdSchema }).strict();
var McpListReportedMonitorsSchema = z5.object({}).strict();
var McpGetReportedMonitorSchema = McpGetMonitorSchema;
var McpListReportedRunsSchema = z5.object({
  monitorId: McpMonitorIdSchema,
  limit: z5.number().int().min(1).max(100).optional().default(20),
  cursor: z5.string().min(1).max(2048).optional()
}).strict();

// src/schemas/queries/contact.ts
import { z as z6 } from "zod";
var GetContactsSchema = z6.object({
  organizationId: z6.string().min(1),
  limit: z6.number().positive().max(1e3).default(100).optional(),
  offset: z6.number().min(0).default(0).optional()
});
var GetContactByIdSchema = z6.object({
  id: z6.number().int().positive(),
  organizationId: z6.string().min(1)
});

// src/schemas/queries/monitor.ts
import { z as z7 } from "zod";
var GetMonitorsSchema = z7.object({
  limit: z7.number().int().min(1).max(100).optional(),
  offset: z7.number().int().min(0).optional(),
  organizationId: z7.string().min(1).optional()
  // Better Auth uses string IDs
});
var GetMonitorsToCheckSchema = z7.object({
  limit: z7.number().int().min(1).max(100).optional().default(10)
});
var GetMeasurementsSchema = z7.object({
  monitorId: z7.number().int().positive(),
  limit: z7.number().int().min(1).max(1e3).optional().default(100),
  offset: z7.number().int().min(0).optional(),
  startTime: z7.date().optional(),
  endTime: z7.date().optional()
});
var GetMonitorByIdSchema = z7.object({
  id: z7.number().int().positive(),
  organizationId: z7.string().min(1)
  // Better Auth uses string IDs
});

// src/schemas/reporting.ts
import { z as z8 } from "zod";
var boundedId = z8.string().min(1).max(256);
var utcTimestamp = z8.iso.datetime({ offset: true }).refine((value) => /(?:Z|\+00:00)$/.test(value));
var contextSchema = z8.record(z8.string().min(1).max(64), z8.string().max(256)).refine((value) => Object.keys(value).length <= 8);
var runEventSchema = z8.strictObject({
  version: z8.literal(1),
  eventId: boundedId,
  monitorId: z8.number().int().positive().safe(),
  runId: boundedId,
  type: z8.enum(["run.created", "run.started", "run.finished"]),
  occurrenceAt: utcTimestamp,
  occurredAt: utcTimestamp,
  outcome: z8.enum(["success", "failure"]).optional(),
  context: contextSchema.optional(),
  reasonCode: z8.string().min(1).max(128).optional()
}).superRefine((event, ctx) => {
  const terminal = event.type === "run.finished";
  if (terminal !== (event.outcome !== void 0) || !terminal && event.reasonCode !== void 0) {
    ctx.addIssue({ code: "custom", message: "Incompatible lifecycle fields" });
  }
  if (Date.parse(event.occurredAt) < Date.parse(event.occurrenceAt)) {
    ctx.addIssue({ code: "custom", message: "Event precedes occurrence" });
  }
});
var reporterHeartbeatSchema = z8.strictObject({
  version: z8.literal(1),
  agentVersion: z8.string().min(1).max(128),
  scanStatus: z8.enum(["ok", "error", "starting"]),
  lastSuccessfulScanAt: utcTimestamp.optional(),
  errorCode: z8.string().min(1).max(128).optional()
}).superRefine((heartbeat, ctx) => {
  if (heartbeat.scanStatus === "ok" && !heartbeat.lastSuccessfulScanAt) {
    ctx.addIssue({ code: "custom", message: "Successful scan time is required" });
  }
  if (heartbeat.scanStatus !== "error" && heartbeat.errorCode !== void 0) {
    ctx.addIssue({ code: "custom", message: "Error code requires failed scan" });
  }
});

// src/types/contact.ts
var VERIFICATION_LIMITS = {
  email: {
    cooldown_minutes: 5,
    daily_cap: 5,
    lifetime_cap: 20,
    code_ttl_minutes: 30,
    max_code_attempts: 5
  },
  sms: {
    cooldown_minutes: 2,
    daily_cap: 3,
    lifetime_cap: 10,
    code_ttl_minutes: 10,
    max_code_attempts: 5
  },
  telegram: {
    cooldown_minutes: 2,
    daily_cap: 5,
    lifetime_cap: 20,
    code_ttl_minutes: 30,
    max_code_attempts: 5
  }
};

// src/types/quota.ts
var QUOTA_KEYS = ["probe", "contact"];
var DEFAULT_QUOTA_LIMITS = {
  probe: 20,
  contact: 20
};

// src/index.ts
var VERSION = "0.0.0";
function formatDate(date) {
  return date.toISOString();
}
export {
  BillingStatusResponseSchema,
  BillingSubscriptionStatusSchema,
  BillingUsageKindSchema,
  CreateBillingPortalSessionBodySchema,
  CreateBillingPortalSessionResponseSchema,
  CreateCheckoutSessionBodySchema,
  CreateCheckoutSessionResponseSchema,
  CreateContactSchema,
  CreateMeasurementSchema,
  CreateMonitorSchema,
  DEFAULT_QUOTA_LIMITS,
  DeleteContactSchema,
  GetContactByIdSchema,
  GetContactsSchema,
  GetMeasurementsSchema,
  GetMonitorByIdSchema,
  GetMonitorsSchema,
  GetMonitorsToCheckSchema,
  InviteToOrganizationSchema,
  LoginSchema,
  MCP_SCOPES,
  McpConsentDecisionSchema,
  McpCreateMonitorSchema,
  McpGetExecutionSchema,
  McpGetMonitorHistorySchema,
  McpGetMonitorSchema,
  McpGetReportedMonitorSchema,
  McpListMonitorsSchema,
  McpListReportedMonitorsSchema,
  McpListReportedRunsSchema,
  McpMonitorIdSchema,
  McpMonitorMutationSchema,
  McpRequestIdSchema,
  McpScopeSchema,
  McpUpdateMonitorDetailsSchema,
  McpUpdateMonitorScheduleSchema,
  PostUsageBatchResponseSchema,
  QUOTA_KEYS,
  QueueEmailSchema,
  RecordUsageBodySchema,
  RecordUsageResponseSchema,
  RegisterSchema,
  RequestContactTestMessageSchema,
  RequestVerificationSchema,
  ResetPasswordRequestSchema,
  ResetPasswordSchema,
  StripeWebhookEventSchema,
  SubmitVerificationCodeSchema,
  UpdateContactSchema,
  UpdateMonitorDetailsSchema,
  UpdateMonitorScheduleSchema,
  UsageEventStatusSchema,
  VERIFICATION_LIMITS,
  VERSION,
  formatDate,
  reporterHeartbeatSchema,
  runEventSchema
};
//# sourceMappingURL=index.js.map